/* eslint-disable no-nested-ternary */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useFeedbackHistory } from '@/api/generated';
import type { MessageResponse } from '@/api/generated.schemas';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { ChatUser } from '@/components/ui/chat-avatar';
import { ChatMessage } from '@/components/ui/chat-message';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Spinner } from '@/components/ui/spinner';

interface FeedbackPanelProps {
  threadId: string;
}

const Message = ({ message }: { message: MessageResponse }) => (
  <ChatMessage
    key={message.id}
    side={message.role === 'user' ? 'right' : 'left'}
    variant={'default'}>
    {message.content[0]?.text}

    <ChatMessage.Avatar
      size={'md'}
      user={message.role === 'user' ? ChatUser.USER : ChatUser.BOT}
    />
  </ChatMessage>
);

const FeedbackPanel = ({ threadId }: FeedbackPanelProps) => {
  const { data, isLoading } = useFeedbackHistory(threadId);
  const { t } = useTranslation();
  if (isLoading) return <Spinner />;
  return (
    <ScrollArea>
      <div className='flex flex-col gap-4'>
        {data?.map((feedback) => (
          <Card key={feedback.id}>
            <CardContent>
              {feedback.previous && <Message message={feedback.previous} />}
              <Message message={feedback.message} />
              {feedback.next && <Message message={feedback.next} />}
            </CardContent>
            <CardFooter className='flex items-center gap-2'>
              <Badge
                variant={
                  feedback.score > 0.5
                    ? 'success'
                    : feedback.score < 0.5
                      ? 'destructive'
                      : 'warning'
                }>
                {feedback.score > 0.5
                  ? t('feedbackAgent.feedback.positive')
                  : feedback.score < 0.5
                    ? t('feedbackAgent.feedback.negative')
                    : t('feedbackAgent.feedback.mixed')}
              </Badge>
              &quot;{feedback.feedback}&quot;
            </CardFooter>
          </Card>
        ))}
      </div>
    </ScrollArea>
  );
};

export default FeedbackPanel;
